import type { GenerationMetadata, LibraryItem } from '@/shared/types';
import { getJson, resolveApiUrl } from '@/shared/api/apiClient';
import {
  LIBRARY_BACKEND,
  identityHeaders,
  libraryPath,
  normalizeMode,
  normalizeStatus,
  normalizeStyle,
  type TaskApiItem,
} from '@/shared/api/backendContract';

const FALLBACK_DESCRIPTION = 'Generated with Valoi Engine';

// Truncates text to `max` chars, appending an ellipsis ONLY when it actually had
// to cut (so a short string is returned untouched, never "short…").
export function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// Builds a short card description from raw source text (140-char truncate with
// an ellipsis), falling back to a placeholder when there is no text.
export function describeFromText(text?: string): string {
  if (!text) return FALLBACK_DESCRIPTION;
  return truncate(text, 140);
}

// Derives a display title from the source text (first non-empty line, truncated)
// for when the user leaves the title blank. The backend independently auto-detects
// its own title from the same text; this just gives the in-session queue and the
// "ready" notification something meaningful to show immediately instead of a bare
// placeholder, until the backend's title syncs in.
export function titleFromText(text: string): string {
  const firstLine = text
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  return firstLine ? truncate(firstLine, 60) : 'Untitled';
}

// Field-level metadata merge: a backend-provided value wins, otherwise keep the
// existing one. A whole-object `??` can't do this — the status/list endpoints
// always return a metadata *object* (with mostly undefined fields today), so it
// would replace the richer existing metadata with an all-empty one. Used by the
// library merge, the status poller, and the detail fetch so none of them clobber
// the rich metadata captured at submit time.
export function mergeMetadata(
  local?: GenerationMetadata,
  api?: GenerationMetadata,
): GenerationMetadata | undefined {
  if (!local) return api;
  if (!api) return local;
  return {
    sessionId: api.sessionId ?? local.sessionId,
    userId: api.userId ?? local.userId,
    modelName: api.modelName ?? local.modelName,
    duration: api.duration ?? local.duration,
    maxScenes: api.maxScenes ?? local.maxScenes,
    aspectRatio: api.aspectRatio ?? local.aspectRatio,
    provider: api.provider ?? local.provider,
    mode: api.mode ?? local.mode,
    style: api.style ?? local.style,
    promptList: api.promptList ?? local.promptList,
    lastPrompt: api.lastPrompt ?? local.lastPrompt,
    lastSceneIdx: api.lastSceneIdx ?? local.lastSceneIdx,
  };
}

/**
 * Whether two metadata objects carry the same information.
 *
 * Reference equality cannot answer that here, and callers who tried got a
 * guard that was simply always true: a status response builds a fresh object
 * literal every call and a cached entry comes out of JSON.parse, so the two
 * are never the same object however identical their contents. That turned a
 * "only write when something changed" check into an unconditional write of the
 * whole library array on every detail fetch.
 *
 * Shallow by design — GenerationMetadata is flat apart from `promptList`,
 * which is compared element-wise. Keys are taken from both sides so a field
 * present-but-undefined (mergeMetadata writes those) matches the same field
 * absent (JSON.stringify drops those).
 */
export function sameMetadata(a?: GenerationMetadata, b?: GenerationMetadata): boolean {
  if (a === b) return true;
  if (!a || !b) return false;

  for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
    const left: unknown = a[key as keyof GenerationMetadata];
    const right: unknown = b[key as keyof GenerationMetadata];
    if (Array.isArray(left) || Array.isArray(right)) {
      if (!Array.isArray(left) || !Array.isArray(right)) return false;
      if (left.length !== right.length) return false;
      if (left.some((value, index) => value !== right[index])) return false;
      continue;
    }
    if (left !== right) return false;
  }
  return true;
}

// A library item as the backend returned it: the display fields the API may omit
// today (title/description/createdAt) are left undefined so the cache merge can
// tell a real backend value from a gap. Call applyLibraryDefaults() to render an
// API-only item (one with no cached counterpart).
export type ApiLibraryItem = Omit<LibraryItem, 'title' | 'description' | 'createdAt'> & {
  title?: string;
  description?: string;
  createdAt?: string;
};

// Fills the display fields the backend may omit so an API-only item still renders
// (id-prefix title, placeholder description, "now" timestamp).
export function applyLibraryDefaults(item: ApiLibraryItem): LibraryItem {
  return {
    ...item,
    title: item.title || item.id.slice(0, 8),
    description: item.description || FALLBACK_DESCRIPTION,
    createdAt: item.createdAt || new Date().toISOString(),
  };
}

type LibraryApiItem = {
  job_id: string;
  title?: string;
  description?: string;
  status: string;
  video_url?: string;
  provider?: string;
  model_name?: string;
  aspect_ratio?: string;
  duration?: number;
  max_scenes?: number | null;
  session_id?: string;
  user_id?: string;
  raw_text?: string;
  prompt_list?: string[];
  created_at?: string;
};

type LibraryApiResponse = {
  items: LibraryApiItem[];
};

function toApiItem(item: LibraryApiItem): ApiLibraryItem {
  const metadata: GenerationMetadata = {
    sessionId: item.session_id,
    userId: item.user_id,
    modelName: item.model_name,
    duration: item.duration,
    maxScenes: item.max_scenes,
    aspectRatio: item.aspect_ratio,
    provider: item.provider,
    promptList: item.prompt_list,
  };

  return {
    id: item.job_id,
    title: item.title,
    description: item.description,
    status: normalizeStatus(item.status),
    videoUrl: resolveApiUrl(item.video_url),
    sourceText: item.raw_text,
    aspectRatio: item.aspect_ratio,
    // `duration` stays in metadata and out of durationSeconds — see taskToApiItem.
    createdAt: item.created_at,
    metadata,
  };
}

// Maps the /api/v1/videos shape onto the raw library item. The minimal fields
// available today (task_id/status/download_url) map directly; the requested
// enrichments (title, text_input, created_at, …) are left undefined until the
// backend ships them — at which point the cache merge adopts them automatically.
function taskToApiItem(item: TaskApiItem): ApiLibraryItem {
  /*
   * `text_input` on the LIST is a lossy preview: the engine cuts it at 280
   * characters with no ellipsis and no flag saying it did (see
   * text_api._TEXT_INPUT_LIST_TRUNCATE_LEN). Only GET /videos/{id} returns the
   * real thing.
   *
   * So it must not become `sourceText`. The merge in libraryService treats the
   * backend as authoritative — `apiItem.sourceText ?? local.sourceText` — so
   * feeding the preview in there means opening a video (detail fetch, full text
   * cached) and then returning to the library (list refresh) silently replaces
   * the cached source with a 280-character stub, which the content page's
   * Original text panel then renders on any later load where the detail fetch
   * has not landed yet.
   *
   * A card description is all the preview is good for, and `describeFromText`
   * cuts to 140 anyway, so nothing on this surface loses anything.
   */
  const textPreview = item.text_input;

  return {
    id: item.task_id,
    title: item.title,
    description: textPreview ? describeFromText(textPreview) : undefined,
    status: normalizeStatus(item.status),
    videoUrl: resolveApiUrl(item.video_url ?? item.download_url),
    thumbnailUrl: item.thumbnail_url ? resolveApiUrl(item.thumbnail_url) : undefined,
    vttUrl: item.vtt_url ? resolveApiUrl(item.vtt_url) : undefined,
    aspectRatio: item.aspect_ratio,
    /*
     * `durationSeconds` is deliberately NOT fed from `item.duration`, even though
     * the names invite it. The API's `duration` is the *requested seconds per
     * scene* (5 or 10) — a generation setting, which is why it belongs in
     * `metadata` below and does there. `durationSeconds` is what LibraryCard
     * badges as the video's runtime, so wiring the two together labelled every
     * card 0:10 regardless of how long the video actually was.
     *
     * `runtime_seconds` is the real thing, measured off the assembled file. Null
     * for anything finished before that column existed, and the card then falls
     * back to reading the loaded <video> element, exactly as it did before.
     */
    durationSeconds: item.runtime_seconds ?? undefined,
    createdAt: item.created_at,
    metadata: {
      aspectRatio: item.aspect_ratio,
      duration: item.duration,
      provider: item.provider,
      mode: normalizeMode(item.mode),
      style: normalizeStyle(item.style),
      promptList: item.prompt_list,
    },
  };
}

// One request's worth of library items. The engine caps `limit` at 200 and
// defaults to 100; asking for its default keeps the two in step without pinning
// the client to a number the server may want to lower.
const LIBRARY_PAGE_SIZE = 100;

// A library big enough to hit this is a bug somewhere else — a cursor that
// stopped advancing, or a server ignoring `before` — and looping forever on it
// would hang the page rather than show the problem.
const LIBRARY_MAX_PAGES = 100;

/**
 * Walk `GET /api/v1/videos` to the end of the user's library.
 *
 * The route is paginated (it used to return every job ever made, with no LIMIT).
 * It still answers with a bare array, so the end of the list is a short page,
 * and the cursor is the last item's `created_at` and `task_id` — the same pair
 * the server orders by, joined the way `_parse_list_cursor` splits it. Keyset,
 * not offset, so a job submitted mid-walk cannot make a page skip or repeat.
 */
async function requestLibraryFromApi(userId?: string): Promise<ApiLibraryItem[]> {
  if (LIBRARY_BACKEND === 'videos') {
    const tasks: TaskApiItem[] = [];
    let before: string | undefined;

    for (let page = 0; page < LIBRARY_MAX_PAGES; page += 1) {
      const query = new URLSearchParams({ limit: String(LIBRARY_PAGE_SIZE) });
      if (before) query.set('before', before);
      // Identity comes from the Bearer JWT; identityHeaders is empty today.
      const items = await getJson<TaskApiItem[]>(
        `${libraryPath(userId)}?${query.toString()}`,
        { headers: identityHeaders(userId) },
      );
      tasks.push(...items);

      if (items.length < LIBRARY_PAGE_SIZE) break;
      const last = items[items.length - 1];
      // Without a created_at there is no cursor to build, and asking again
      // without one would re-fetch page 1 forever.
      if (!last?.created_at) break;
      before = `${last.created_at}|${last.task_id}`;
    }

    return tasks.map(taskToApiItem);
  }

  const response = await getJson<LibraryApiResponse>(libraryPath(userId));
  return response.items.map(toApiItem);
}

// Coalesce concurrent identical list fetches into one in-flight request. On a
// library visit, ProcessingProvider (job hydration) and LibraryPage both fetch
// the same /api/v1/videos data — and React StrictMode double-invokes each effect
// in dev — so the same data was being fetched 2-3x at once. Only the pending
// promise is shared (keyed by user); once it settles, a later call refetches, so
// this dedups the burst without serving stale data on subsequent refreshes.
const inFlightByUser = new Map<string, Promise<ApiLibraryItem[]>>();

export function fetchLibraryFromApi(userId?: string): Promise<ApiLibraryItem[]> {
  const key = userId ?? '';
  const pending = inFlightByUser.get(key);
  if (pending) return pending;

  const request = requestLibraryFromApi(userId).finally(() => {
    inFlightByUser.delete(key);
  });
  inFlightByUser.set(key, request);
  return request;
}
